/**
 * star_view — the star of a derived system and everything inside its footprint (the natural object layer, 2026-10-07).
 *
 * WHAT IT IS. `star_look.ts` says what each kind of star looks like and does; this file makes the sector draw it:
 *  · THE PRIMARY is the sector's own sun rig (space_sun.ts) given the kind's SURFACE — limb law, granules, spots, a
 *    turning sphere, an oblate outline, flare patches — and its true colour. `frame()` hands SpaceModule the rig's
 *    radius, emission and corona and the KEY LIGHT's colour and scale.
 *  · A CLOSE COMPANION is a second body (space_sun.starBody) on the same shader, orbiting the barycentre on the WALL
 *    clock (every client sees the same phase). The two discs are additive quads at one depth, so neither can hide the
 *    other: each is TOLD where the nearer one stands (an occluder disc in its own quad's frame) and draws nothing there.
 *    The key light dims by exactly the flux that is covered — an eclipse is real.
 *  · EVERYTHING ELSE stellar (discs, streams, jets, shells — stellar_parts.ts) hangs on the same clock and the same bodies.
 *
 * THE KEY LIGHT IS NOT THE DISC. The disc keeps the star's true colour (an M dwarf is orange). The light on the hulls
 * is that colour moved KEY_ADAPT of the way to white: an eye adapts to its illuminant, and a hull must stay readable.
 * SpaceModule sets the rig's colour from the frame's `rgb` (the key's); `place()` — called right after — sets the true
 * one back on the rig.
 *
 * THE RIG GOES BACK. `dispose()` (the controller calls it on a fault too) resets the rig to the Sun's surface and takes
 * the companion off the root: the sector's old star look is whole again.
 */
import * as THREE from 'three/webgpu';
import { SUN_DISC_EMISSION, angularRadius } from '../space_look.js';
import {
  sunRig, starBody, placeBody, placeSun, setSunLook, setBodyLook, setBodySurface, setBodySpin, setBodyShape, setBodyNose,
  setBodyPatch, setBodyOccluder, resetBody, resetSunSurface, discUnitM, SUN_QUAD_D, type StarBody,
} from '../space_sun.js';
import {
  STAR_LOOK, starRgb, keyRgb, starRadiusM, surfaceOf, starPulse, glareGain, pairSpec, pairAt, pairEclipse, pairReachM, mixKeyLight, pixelFloorM,
  starHash01, KEY_LIGHT_FLOOR, KEY_LIGHT_BURST, type PairSpec, type Rgb, type StarPulse, type StarSurface, type V3, type LightPart,
} from './star_look.js';
import type { NaturalEnv, StarFrame, StarPlace, StarView } from './natural_api.js';
import { STELLAR_REACH, type StarKind, type SystemMakeup } from './natural_types.js';
import { buildStellarParts, type StellarPart, type StellarScene } from './stellar_parts.js';

export { starChartHex } from './star_look.js';

/** A steady star's key light never passes this (lit hulls stay under the bloom threshold); a flare or a nova may, to KEY_LIGHT_BURST. */
const KEY_STEADY_CAP = 1.12;
/** A compact star is DRAWN hundreds of times its true size (a white dwarf at 6 km beside a 34 km dwarf star; the real ratio
 *  is ~1 : 100), so its drawn area overstates its light: beside a companion it lights a hull with this share of that area. */
const COMPACT_KEY_SHARE = 0.12;
/** A point of light is drawn no smaller than this many pixels (its emission falls with the area it gained: the flux holds). */
const POINT_FLOOR_PX = 1.6;

/** One star as the view tracks it. */
export interface TrackedStar {
  kind: StarKind; seed: number;
  surface: StarSurface;
  /** Its drawn radius before any pulsation (m) and now. */
  baseR: number; radiusM: number;
  /** Its centre (sector metres; the barycentre is the origin). */
  abs: THREE.Vector3;
  /** Its spin axis and zero meridian (unit, sector frame). */
  axis: THREE.Vector3; x0: THREE.Vector3; z0: THREE.Vector3;
  pulse: StarPulse;
  /** True colour (linear, max 1), × SUN_DISC_EMISSION, corona gain — as drawn this frame. */
  rgb: Rgb; emission: number; corona: number;
  /** The share of its disc in view past the other star (the ship's eye for the light, the camera's for the picture). */
  visShip: number; visCam: number;
  /** A flare's light as this viewer gets it (0 when it burns on the far side). */
  flareSeen: number;
  body: StarBody;
  /** Where it is from the camera this frame (set in place()). */
  dir: THREE.Vector3; dist: number; angR: number;
  /** Extra dimming of its DISC this frame (dust in the line of sight; parts write it). */
  veil: number;
}

class StellarView implements StarView, StellarScene {
  readonly stars: TrackedStar[] = [];
  readonly pair: PairSpec | null;
  readonly group = new THREE.Group();
  /** Lights the parts add to the key (an accretion disc in outburst, a nova's fireball). */
  readonly lights: LightPart[] = [];
  nowS = 0;
  private readonly parts: StellarPart[];
  private readonly keyOut: [number, number, number] = [1, 1, 1];
  private readonly frameOut: StarFrame = { rgb: this.keyOut, radiusM: 1, emission: 1, corona: 1, lightScale: 1 };
  private fovY = (60 * Math.PI) / 180;
  private heightPx = 1080;
  private lastDist = 0;
  private share = 1;
  private lightScale = 1;
  private disposed = false;
  private readonly pa: V3 = [0, 0, 0];
  private readonly pb: V3 = [0, 0, 0];
  private readonly _right = new THREE.Vector3();
  private readonly _up = new THREE.Vector3();
  private readonly _fwd = new THREE.Vector3();
  private readonly _v = new THREE.Vector3();
  private readonly _e = new THREE.Vector3();
  private readonly _yp = new THREE.Vector3();
  private readonly _zv = new THREE.Vector3();
  private readonly _xs = new THREE.Vector3();
  private readonly _zs = new THREE.Vector3();
  private readonly _rows: [number, number, number, number, number, number, number, number, number] = [1, 0, 0, 0, 1, 0, 0, 0, 1];
  private readonly _parts: LightPart[] = [];

  constructor(readonly env: NaturalEnv, readonly makeup: SystemMakeup) {
    const F = makeup.footprintM;
    this.pair = makeup.companion ? pairSpec(makeup.config, makeup.primary.kind, makeup.companion.kind, F, makeup.primary.seed) : null;
    this.group.name = 'stellar';
    const rig = sunRig();
    this.stars.push(this.track(makeup.primary.kind, makeup.primary.seed, this.pair ? this.pair.ra : starRadiusM(makeup.primary.kind, F), rig));
    if (this.pair && makeup.companion) {
      const b = starBody(0);
      this.stars.push(this.track(makeup.companion.kind, makeup.companion.seed, this.pair.rb, b));
      env.root()?.add(b.group);
    }
    env.root()?.add(this.group);
    this.parts = buildStellarParts(this);
  }

  private track(kind: StarKind, seed: number, radiusM: number, body: StarBody): TrackedStar {
    // the spin axis: the system's normal, tipped up to ~25° toward a seeded side
    const tilt = starHash01(`${seed}:tilt`) * 0.44, az = starHash01(`${seed}:taz`) * Math.PI * 2;
    const axis = new THREE.Vector3(Math.sin(tilt) * Math.cos(az), Math.cos(tilt), Math.sin(tilt) * Math.sin(az)).normalize();
    const x0 = new THREE.Vector3(1, 0, 0).addScaledVector(axis, -axis.x).normalize();
    const z0 = new THREE.Vector3().crossVectors(x0, axis).normalize();
    const L = STAR_LOOK[kind];
    return {
      kind, seed, surface: surfaceOf(kind), baseR: radiusM, radiusM, abs: new THREE.Vector3(), axis, x0, z0,
      pulse: starPulse(kind, seed, 0), rgb: starRgb(kind), emission: L.emission, corona: L.corona,
      visShip: 1, visCam: 1, flareSeen: 0, body, dir: new THREE.Vector3(0, 0, -1), dist: 1, angR: 0.01, veil: 1,
    };
  }

  /** The star's own state at this instant: where it is, how big, how bright, what colour. */
  private advance(s: TrackedStar, nowS: number, ship: { x: number; y: number; z: number }): void {
    const L = STAR_LOOK[s.kind];
    const p = (s.pulse = starPulse(s.kind, s.seed, nowS));
    s.rgb = starRgb(s.kind, p.kelvin);
    let r = s.baseR * p.radius, emission = L.emission * p.emission;
    // a point of light keeps a floor on screen; the light it spreads over the wider disc is the same light
    if (L.sizeM !== undefined && this.lastDist > 0) {
      const floor = pixelFloorM(this.lastDist, this.fovY, this.heightPx, POINT_FLOOR_PX);
      if (floor > r) { emission *= Math.max(0.08, (r * r) / (floor * floor)); r = floor; }
    }
    // a flare lights only the side it burns on
    s.flareSeen = 0;
    if (p.flare.amp > 0) {
      const cl = Math.cos(p.flare.lat), lon = p.flare.lon + p.spin;
      const f = this._v.copy(s.x0).multiplyScalar(cl * Math.cos(lon)).addScaledVector(s.axis, Math.sin(p.flare.lat)).addScaledVector(s.z0, cl * Math.sin(lon));
      const dx = ship.x - s.abs.x, dy = ship.y - s.abs.y, dz = ship.z - s.abs.z, d = Math.hypot(dx, dy, dz) || 1;
      const facing = (f.x * dx + f.y * dy + f.z * dz) / d;
      s.flareSeen = p.flare.amp * Math.max(0, Math.min(1, (facing + 0.2) / 0.7));
      // a point of light has no face for a patch to ride: the whole point brightens
      if (L.sizeM !== undefined) { emission *= 1 + 2.2 * p.flare.amp; s.flareSeen = p.flare.amp; }
    }
    s.radiusM = r; s.emission = emission; s.corona = L.corona;
  }

  frame(dt: number, nowS: number): StarFrame {
    this.nowS = nowS;
    const ship = this.env.shipAbs();
    const a = this.stars[0], b = this.stars[1] ?? null;
    if (this.pair && b) {
      pairAt(this.pair, nowS, this.pa, this.pb);
      a.abs.set(this.pa[0], this.pa[1], this.pa[2]); b.abs.set(this.pb[0], this.pb[1], this.pb[2]);
    }
    for (const s of this.stars) { s.veil = 1; this.advance(s, nowS, ship); }
    a.visShip = 1; if (b) b.visShip = 1;
    if (b) {
      const e = pairEclipse(ship, this.pa, a.radiusM, this.pb, b.radiusM);
      a.visShip = e.visA; b.visShip = e.visB;
    }
    // the parts move with the same clock (and may veil a star or add a light of their own)
    this.lights.length = 0; this.lightsBase = 0;
    for (const part of this.parts) part.frame?.(dt, nowS);
    // ONE key light: every source's visible flux, in view
    const parts = this._parts; parts.length = 0;
    let burst = false, base = 0;
    for (const s of this.stars) {
      const L = STAR_LOOK[s.kind];
      const lit = Math.max(0, s.pulse.light - s.pulse.flare.amp) + s.flareSeen;   // its quiet light + the flare as seen from here
      // the true disc's flux (never the pixel floor's): surface brightness × area × what it is doing now
      parts.push({ rgb: s.rgb, flux: L.emission * s.baseR * s.baseR * lit * (L.sizeM !== undefined ? COMPACT_KEY_SHARE : 1), vis: s.visShip * s.veil });
      if (s.flareSeen > 0.05 || s.pulse.burstS < 60) burst = true;
      base = Math.max(base, L.light * lit);
    }
    for (const l of this.lights) { parts.push(l); if (l.flux > 0) burst = true; }
    const mix = mixKeyLight(parts, Math.max(base, this.lightsBase), KEY_LIGHT_FLOOR, burst ? KEY_LIGHT_BURST : KEY_STEADY_CAP);
    const key = keyRgb(mix.rgb);
    this.keyOut[0] = key[0]; this.keyOut[1] = key[1]; this.keyOut[2] = key[2];
    this.share = mix.share; this.lightScale = mix.scale;
    const out = this.frameOut;
    out.radiusM = Math.min(a.radiusM, this.makeup.footprintM * STELLAR_REACH);
    out.emission = a.emission * a.veil;
    out.corona = a.corona * Math.min(1, a.veil + 0.25);
    out.lightScale = mix.scale;
    return out;
  }
  /** What the parts' lights are worth as a key base (a nova outshines its stars). */
  lightsBase = 0;

  /** The turn of a body's sphere and its outline, for a viewer looking along `d` (camera → star). */
  private seatSurface(s: TrackedStar, d: THREE.Vector3): void {
    const L = STAR_LOOK[s.kind];
    const zv = this._zv.copy(d).negate();
    const cosI = s.axis.dot(zv);
    const yp = this._yp.copy(s.axis).addScaledVector(zv, -cosI);
    let sinI = yp.length();
    const qr = s.body.frameRight, qu = s.body.frameUp; // the quad's own frame (THE WIDE-QUAD LAW): the camera's for a small star
    if (sinI < 1e-4) { yp.copy(qu).addScaledVector(zv, -qu.dot(zv)); sinI = 0; }
    yp.normalize();
    const e = this._e.crossVectors(yp, zv);
    const ph = s.pulse.spin, c = Math.cos(ph), sn = Math.sin(ph);
    const xs = this._xs.copy(s.x0).multiplyScalar(c).addScaledVector(s.z0, sn);
    const zs = this._zs.copy(s.z0).multiplyScalar(c).addScaledVector(s.x0, -sn);
    const r = this._rows;
    r[0] = xs.dot(e); r[1] = xs.dot(yp); r[2] = xs.dot(zv);
    r[3] = s.axis.dot(e); r[4] = s.axis.dot(yp); r[5] = cosI;
    r[6] = zs.dot(e); r[7] = zs.dot(yp); r[8] = zs.dot(zv);
    setBodySpin(s.body, r);
    setBodyShape(s.body, yp.dot(qr), yp.dot(qu), Math.sqrt(L.oblate * L.oblate * sinI * sinI + cosI * cosI), L.gravDark);
    // a flare's patch rides the surface (the star's own frame: x0 / axis / z0 at its zero phase — the rows carry the turn)
    const f = s.pulse.flare;
    if (f.amp > 0) { const cl = Math.cos(f.lat); setBodyPatch(s.body, 0, cl * Math.cos(f.lon), Math.sin(f.lat), cl * Math.sin(f.lon), Math.min(6, f.amp * 3.2)); } else setBodyPatch(s.body, 0, 0, 0, 1, 0);
  }

  place(p: StarPlace): void {
    if (this.disposed) return;
    this.fovY = (p.cam.fov * Math.PI) / 180; this.heightPx = p.heightPx; this.lastDist = p.starDist;
    const o = this.env.origin();
    this.group.position.set(-o.x, -o.y, -o.z); // the star frame: children sit at sector metres about the barycentre
    const right = this._right.set(1, 0, 0).applyQuaternion(p.camQuat), up = this._up.set(0, 1, 0).applyQuaternion(p.camQuat);
    const fwd = this._fwd.set(0, 0, -1).applyQuaternion(p.camQuat);
    const a = this.stars[0], b = this.stars[1] ?? null;
    for (const s of this.stars) {
      s.dir.set(s.abs.x - p.camAbs.x, s.abs.y - p.camAbs.y, s.abs.z - p.camAbs.z);
      s.dist = Math.max(1, s.dir.length());
      s.dir.divideScalar(s.dist);
      s.angR = angularRadius(s.radiusM, s.dist);
      s.visCam = 1;
    }
    if (b) {
      const e = pairEclipse(p.camAbs, [a.abs.x, a.abs.y, a.abs.z], a.radiusM, [b.abs.x, b.abs.y, b.abs.z], b.radiusM);
      a.visCam = e.visA; b.visCam = e.visB;
    }
    for (const s of this.stars) {
      const vis = Math.max(0, Math.min(1, p.visible * s.visCam * s.veil));
      s.body.glareScale = glareGain(s.emission * s.veil, s.angR);
      // the distance rides along: a black-hole lens bends a star by where it STANDS (space_sun.starLensSource)
      if (s === a) placeSun(p.camLocal, p.camQuat, s.dir, s.angR, vis, s.dist); else placeBody(s.body, p.camLocal, p.camQuat, s.dir, s.angR, vis, s.dist);
      const e = SUN_DISC_EMISSION * s.emission * s.veil;
      if (s === a) setSunLook(s.rgb, e, s.corona * Math.min(1, s.veil + 0.25)); else setBodyLook(s.body, s.rgb, e, s.corona * Math.min(1, s.veil + 0.25));
      setBodySurface(s.body, s.surface);
      this.seatSurface(s, s.dir);
      setBodyOccluder(s.body, null);
      setBodyNose(s.body, 1, 0, 0, 2);
    }
    if (b) this.occlude(a, b, fwd);
    for (const part of this.parts) part.place(p, right, up, fwd);
  }

  /** Each star of a pair is told where the other stands on ITS quad; the nearer takes the overlap (cross-fading where they are level). */
  private occlude(a: TrackedStar, b: TrackedStar, fwd: THREE.Vector3): void {
    const fa = a.dir.dot(fwd), fb = b.dir.dot(fwd);
    if (fa < 0.05 || fb < 0.05) return; // one of them is beside or behind the eye: nothing overlaps on screen
    // how decisively a is the nearer: level within a few per cent of their sizes → both keep half the overlap
    const level = 0.06 * (a.radiusM + b.radiusM);
    const aNear = Math.max(0, Math.min(1, 0.5 + (b.dist - a.dist) / (2 * level)));
    const seat = (s: TrackedStar, other: TrackedStar, k: number): void => {
      if (k <= 0) return;
      // where the other's sight line meets THIS star's quad — the plane through D·dir with the quad's own normal
      // (THE WIDE-QUAD LAW: the image plane's for a small star, where this is D·fs/fo as it always was)
      const n = s.body.frameN, sn = s.dir.dot(n), on = other.dir.dot(n), ratio = sn / on;
      if (!(ratio > 0) || !(ratio < 20)) return; // the two are a right angle apart or more: nothing of one is on the other's quad
      const unit = discUnitM(s.angR), t = SUN_QUAD_D * ratio;
      const off = this._v.copy(other.dir).multiplyScalar(t).addScaledVector(s.dir, -SUN_QUAD_D);
      const r = discUnitM(other.angR) * ratio;
      setBodyOccluder(s.body, { x: off.dot(s.body.frameRight) / unit, y: off.dot(s.body.frameUp) / unit, r: r / unit, soft: Math.max(0.006, 1.5 / Math.max(4, (s.angR / this.fovY) * this.heightPx)), k });
    };
    seat(a, b, 1 - aNear);
    seat(b, a, aNear);
  }

  state(): Record<string, unknown> {
    const r3 = (v: number): number => Math.round(v * 1000) / 1000;
    return {
      kinds: this.stars.map((s) => s.kind), config: this.makeup.config, showpiece: this.makeup.showpiece,
      stars: this.stars.map((s) => ({
        kind: s.kind, radiusM: Math.round(s.radiusM), kelvin: Math.round(s.pulse.kelvin), light: r3(s.pulse.light), phase: r3(s.pulse.phase),
        emission: r3(s.emission), flare: r3(s.pulse.flare.amp), visShip: r3(s.visShip), visCam: r3(s.visCam), veil: r3(s.veil),
        abs: [Math.round(s.abs.x), Math.round(s.abs.y), Math.round(s.abs.z)], angDeg: r3((s.angR * 180) / Math.PI), rgb: s.rgb.map(r3),
      })),
      key: { rgb: this.keyOut.map(r3), scale: r3(this.lightScale), share: r3(this.share) },
      pair: this.pair ? { sepM: Math.round(this.pair.sepM), periodS: this.pair.periodS, reachM: Math.round(pairReachM(this.pair)), discM: Math.round(this.pair.discM) } : null,
      reachM: Math.round(this.makeup.footprintM * STELLAR_REACH),
      parts: this.parts.map((part) => ({ name: part.name, ...(part.state?.() ?? {}) })),
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const part of this.parts) { try { part.dispose(); } catch { /* a part's own teardown */ } }
    this.group.removeFromParent();
    for (const s of this.stars) { resetBody(s.body); if (s.body !== sunRig()) s.body.group.removeFromParent(); }
    resetSunSurface();
  }
}

export function createStarView(env: NaturalEnv, makeup: SystemMakeup): StarView | null {
  return new StellarView(env, makeup);
}
